// ⚠️ #1522 — the console.warn calls in this file stay warn, deliberately.
//
// main.tsx runs captureConsoleIntegration at levels ["error"], so a warn
// produces no Sentry event. That is the point here: the activity log's own insert
// fails on a timer or a shim, nothing she did depends on it, and nothing is
// waiting on the result. The repo's rule is "warn means expected and handled,
// error means a human should see it" — these are the warns that rule exists
// for, and converting them is the quota flood main.tsx's comment warns about.
//
// A failed WRITE she initiated does not belong here. If one is added to this
// file, report it with reportError.

import { supabase } from "@/integrations/supabase/client";
import { getCurrentUserId } from "./analytics";

export type ActionType =
  | "meal_plan"
  | "kroger_send"
  | "recipe_save"
  | "schedule_import"
  | "email_draft"
  | "brain_dump"
  | "budget_create"
  | "week_built"
  | "todo_saved"
  | "morning_notes"
  | "garden_plan"
  | "packing_list"
  | "gift_list_create"
  | "general_chat";

export const TIME_CREDITS: Record<ActionType, number> = {
  meal_plan: 45,
  kroger_send: 25,
  recipe_save: 8,
  schedule_import: 15,
  email_draft: 15,
  brain_dump: 15,
  budget_create: 12,
  week_built: 20,
  todo_saved: 3,
  morning_notes: 12,
  garden_plan: 15,
  packing_list: 15,
  // 15 to match packing_list: both are "a list you would otherwise assemble by
  // hand." Kasandra's number, chosen 2026-08-04 (#579) — this feeds her visible
  // time-saved total, so it is a product decision, not an implementation detail.
  // Do not retune it, or any other value here, as a drive-by.
  gift_list_create: 15,
  general_chat: 3,
};

/** Fire-and-forget activity log insert. Never throws, never blocks. */
export async function logActivity(
  actionType: ActionType,
  metadata?: Record<string, unknown>
): Promise<void> {
  try {
    const userId = getCurrentUserId();
    if (!userId) return;
    await supabase.from("activity_log" as never).insert({
      user_id: userId,
      action_type: actionType,
      time_credit: TIME_CREDITS[actionType],
      metadata: (metadata ?? {}) as never,
    } as never);
  } catch (err) {
    console.warn("[activityLog] insert failed (non-fatal):", err);
  }
}

/** Returns true if an activity of this type was logged for the current user today (local midnight). */
export async function hasLoggedToday(actionType: ActionType): Promise<boolean> {
  try {
    const userId = getCurrentUserId();
    if (!userId) return false;
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const { count } = await supabase
      .from("activity_log" as never)
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId)
      .eq("action_type", actionType)
      .gte("created_at", todayStart.toISOString());
    return (count ?? 0) > 0;
  } catch {
    return false;
  }
}
